import type { UseQueryStateOptions } from 'nuqs';
import { useQueryState } from 'nuqs';

/**
 * Wrapper for useQueryState that handles the "Bail out to client-side rendering" error
 *
 * This fix addresses a known issue with nuqs where useQueryState can throw an error
 * "Bail out to client-side rendering: useSearchParams()" during server-side rendering
 * or hydration. This typically happens when the component tries to access search params
 * before the client-side JavaScript has fully loaded.
 *
 * The fix catches this specific error and returns a safe fallback (null state with no-op setter)
 * instead of crashing the application. This prevents the error from bubbling up and
 * allows the component to gracefully handle the SSR/hydration transition.
 *
 * @param key - The query parameter key
 * @param options - Options for the useQueryState hook
 * @returns The same return type as useQueryState, with fallback handling for SSR errors
 */

export const useQueryStateWithoutSuspense = ((key: string, options: UseQueryStateOptions<unknown>) => {
  try {
    return useQueryState(key, options);
  } catch (err) {
    if (String(err).includes(`Bail out to client-side rendering: useSearchParams()`)) {
      return [null, () => {}] as never;
    }
    throw err;
  }
}) as typeof useQueryState;
